import { FEEDBACK_KINDS, FEEDBACK_STATUSES, type AdminFeedback } from '@steado/admin-contract/feedback';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';

import { ConfirmDialog } from '@/components/shell/ConfirmDialog';
import { DataTable, type TableColumn, type TableRow } from '@/components/table/DataTable';
import { ScopePills } from '@/components/table/ScopePills';
import { BadgeCell, RowActionsCell, TextCell } from '@/components/table/cells';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/lib/auth/auth-context';
import { formatDateTime, formatRelative } from '@/lib/format';
import { scopeCountsQueryOptions } from '@/lib/scope-counts-query';

import { FeedbackDrawer } from './FeedbackDrawer';
import {
  FEEDBACK_KIND_LABELS as KIND_LABELS,
  FEEDBACK_KIND_TONES as KIND_TONES,
  FEEDBACK_STATUS_LABELS as STATUS_LABELS,
  FEEDBACK_STATUS_TONES as STATUS_TONES,
  appDetailsOf,
} from './feedback-labels';
import { dismissFeedback, feedbackQueryOptions, resolveFeedback } from './feedback-query';
import type { FeedbackSearch } from './feedback-search';

export interface FeedbackScreenProps {
  readonly search: FeedbackSearch;
  readonly onSearchChange: (next: FeedbackSearch) => void;
  /**
   * Opens the sender's record. Navigating is the Route's job, so this screen
   * renders without a router, the same split as the food-requests screen.
   */
  readonly onOpenUser?: ((userId: string) => void) | undefined;
}

const ADMIN_ONLY = 'Answering feedback requires the admin role.';

/**
 * The message gets half the table. A queue like this is read for what was
 * said, and the other four columns are context for it; the old even split
 * clamped a two-sentence report to a line and a half.
 */
const COLUMNS: readonly TableColumn[] = [
  { key: 'report', label: 'What they said', width: '2.6fr' },
  { key: 'outcome', label: 'Outcome', width: '1.1fr' },
  { key: 'who', label: 'Who sent it', width: '0.9fr' },
  // Wide enough for "16 Sept, 17:26" under the relative time, which 0.7 was not.
  { key: 'sent', label: 'Sent', width: '0.9fr' },
  { key: 'actions', label: '', width: '220px', align: 'right' },
];

/**
 * The message as the table previews it: the sender's line breaks kept, runs
 * of blank lines squashed. Three lines is the budget, and a paragraph gap
 * spending one of them on nothing left a row reading "title, blank, …". The
 * drawer shows the message exactly as written.
 */
function previewOf(message: string): string {
  return message.replace(/\n\s*\n+/g, '\n');
}

/**
 * "Send feedback" (Profile → App section): bug reports and ideas, read by a
 * person on the team.
 *
 * Resolving and dismissing both just close the report with an optional note —
 * unlike a food request, there is nothing here to point at a catalog row.
 * The client sends no screenshot in v1 (no image-picker dependency yet), so
 * this screen has nothing to preview besides the message and the optional
 * self-reported app/device line.
 *
 * The table shows up to three lines of each message; the row opens a drawer
 * with the whole thing, the sender, and the same two answers.
 */
export function FeedbackScreen({ search, onSearchChange, onOpenUser }: FeedbackScreenProps) {
  const { api, can } = useAuth();
  const queryClient = useQueryClient();
  const counts = useQuery(scopeCountsQueryOptions(api));
  const noteId = useId();

  const [opened, setOpened] = useState<AdminFeedback | null>(null);
  const [answering, setAnswering] = useState<{ report: AdminFeedback; as: 'resolve' | 'dismiss' } | null>(
    null,
  );
  const [note, setNote] = useState('');

  const page = useQuery(feedbackQueryOptions(api, search));

  const settled = () => {
    setAnswering(null);
    setNote('');
    // The drawer shows the row as it was listed, so it closes rather than
    // sit there calling a resolved report open until the refetch lands.
    setOpened(null);
    void queryClient.invalidateQueries({ queryKey: ['admin', 'feedback'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'scope-counts'] });
  };

  const answer = useMutation({
    mutationFn: (target: { report: AdminFeedback; as: 'resolve' | 'dismiss' }) => {
      const body = note.trim() === '' ? {} : { note: note.trim() };
      return target.as === 'resolve'
        ? resolveFeedback(api, target.report.id, body)
        : dismissFeedback(api, target.report.id, body);
    },
    onSuccess: settled,
    retry: false,
  });

  const rows: readonly TableRow[] = (page.data?.items ?? []).map((report) => ({
    id: report.id,
    onOpen: () => {
      setOpened(report);
    },
    cells: [
      <div key="report" className="flex min-w-0 flex-col gap-1.5 py-0.5">
        {/*
          Three lines with the sender's own line breaks kept: a bug report is
          often "1. open the day 2. tap the meal 3. it is gone", and flattening
          that reads as one run-on sentence. Past three lines the row opens.
        */}
        <p className="line-clamp-3 text-[13px] leading-relaxed break-words whitespace-pre-line text-foreground">
          {previewOf(report.message)}
        </p>
        <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <BadgeCell label={KIND_LABELS[report.kind]} tone={KIND_TONES[report.kind]} />
          {report.appVersion === null ? null : (
            <span className="truncate font-mono text-xs text-muted-foreground">{appDetailsOf(report)}</span>
          )}
        </span>
      </div>,
      <span key="outcome" className="flex min-w-0 flex-col gap-1.5">
        <BadgeCell label={STATUS_LABELS[report.status]} tone={STATUS_TONES[report.status]} dot />
        {report.adminNote === null ? null : (
          <span className="line-clamp-2 text-xs leading-relaxed text-muted-foreground">
            {report.adminNote}
          </span>
        )}
      </span>,
      <TextCell key="who" value={report.displayName ?? 'Unnamed'} sub={report.phone} />,
      <TextCell key="sent" value={formatRelative(report.createdAt)} sub={formatDateTime(report.createdAt)} />,
      <RowActionsCell key="actions">
        {report.status === 'open' && can.mutate ? (
          <>
            <Button
              variant="outline"
              size="xs"
              onClick={() => {
                setAnswering({ report, as: 'resolve' });
              }}
            >
              Mark resolved
            </Button>
            <Button
              variant="ghost"
              size="xs"
              onClick={() => {
                setAnswering({ report, as: 'dismiss' });
              }}
              className="text-err-fg hover:text-err-fg"
            >
              Dismiss
            </Button>
          </>
        ) : null}
      </RowActionsCell>,
    ],
  }));

  return (
    <div className="flex max-w-[1100px] flex-col gap-3">
      <ScopePills
        label="Report status"
        scopes={FEEDBACK_STATUSES.map((status) => ({
          id: status,
          label: STATUS_LABELS[status],
          ...(counts.data === undefined ? {} : { count: counts.data.feedback[status] }),
        }))}
        value={search.status ?? 'open'}
        onChange={(status) => {
          onSearchChange({ ...search, status, cursor: undefined });
        }}
      />

      <ScopePills
        label="Kind"
        scopes={[
          { id: 'all' as const, label: 'All' },
          ...FEEDBACK_KINDS.map((kind) => ({ id: kind, label: KIND_LABELS[kind] })),
        ]}
        value={search.kind ?? 'all'}
        onChange={(kind) => {
          onSearchChange({ ...search, kind: kind === 'all' ? undefined : kind, cursor: undefined });
        }}
      />

      <p className="text-[13px] leading-relaxed text-muted-foreground">
        Bug reports and ideas sent from Profile. A person on the team reads every one; open a row to read the
        whole message. Resolving or dismissing just closes the report, both with an optional note for whoever
        looks at it next.
      </p>

      {page.isError ? (
        <Alert role="alert" tone="err">
          <AlertDescription>{page.error.message}</AlertDescription>
        </Alert>
      ) : null}

      <DataTable
        caption="Feedback"
        title={search.status === undefined ? 'All' : STATUS_LABELS[search.status]}
        count={rows.length === 0 ? undefined : `showing ${String(rows.length)}`}
        columns={COLUMNS}
        rows={rows}
        isLoading={page.isPending}
        emptyTitle="Nothing waiting"
        emptyBody="No feedback in this scope. For the open queue that is the healthy state."
        footer={
          page.data?.pagination.hasMore === true ? (
            <div className="flex items-center justify-end border-t border-border px-[18px] py-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  onSearchChange({ ...search, cursor: page.data?.pagination.nextCursor ?? undefined });
                }}
              >
                Older reports
              </Button>
            </div>
          ) : undefined
        }
      />

      {can.mutate ? null : <p className="text-xs text-muted-foreground">{ADMIN_ONLY}</p>}

      {opened === null ? null : (
        <FeedbackDrawer
          report={opened}
          canMutate={can.mutate}
          onClose={() => {
            setOpened(null);
          }}
          onOpenUser={onOpenUser}
          onAnswer={(as) => {
            setAnswering({ report: opened, as });
          }}
        />
      )}

      {answering !== null ? (
        <ConfirmDialog
          title={answering.as === 'resolve' ? 'Mark this report resolved?' : 'Dismiss this report?'}
          body={
            <div className="flex flex-col gap-3">
              <p>
                &quot;{answering.report.message.slice(0, 160)}
                {answering.report.message.length > 160 ? '…' : ''}&quot;
                {answering.as === 'resolve'
                  ? ' will be closed as acted on, or confirmed with nothing to do.'
                  : ' will be closed as not actionable — a duplicate, out of scope, or not enough to go on.'}
              </p>
              <div className="flex flex-col gap-2">
                <Label htmlFor={noteId} className="text-xs font-medium text-muted-foreground">
                  Note (optional)
                </Label>
                <Textarea
                  id={noteId}
                  value={note}
                  maxLength={500}
                  rows={3}
                  onChange={(event) => {
                    setNote(event.target.value);
                  }}
                  placeholder="Fixed in 4.13.0"
                />
              </div>
            </div>
          }
          confirmLabel={answering.as === 'resolve' ? 'Mark resolved' : 'Dismiss'}
          destructive={answering.as === 'dismiss'}
          onConfirm={() => {
            answer.mutate(answering);
          }}
          onCancel={() => {
            setAnswering(null);
            setNote('');
            answer.reset();
          }}
          isPending={answer.isPending}
          error={answer.isError ? answer.error.message : undefined}
        />
      ) : null}
    </div>
  );
}
